/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {media, spacing, Visible} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {DescriptionCard} from 'UI/components/modules/DescriptionCard';
import {GuideCard} from 'UI/components/modules/GuideCard';
import {HeroSection} from 'UI/components/modules/HeroSection';
import {ReviewsSection} from 'UI/components/modules/ReviewsSection';
import {StatTileGrid} from 'UI/components/modules/StatTileGrid';
import {StickyActionButton} from 'UI/components/modules/StickyActionButton';
import {WalkMap} from 'UI/components/modules/WalkMap';
import {WaypointPreviewList} from 'UI/components/modules/WaypointPreviewList';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';
import {useWalkDetailPage} from 'UI/hooks/useWalkDetailPage';
import {AppLayout} from 'UI/layouts/AppLayout';

import {LockOpenIcon} from 'Images/icons';
import {getLegalPages} from 'Payload/utils/getLegalPages';
import {abbreviateReviewerName} from 'UI/utils/jsonLd';
import type {WalkReviewSummary} from 'UI/utils/jsonLd';

import type {GetStaticPaths, GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Config, LegalPage, Walk} from 'types/payload-types';

interface WalkPageProps {
    legalPages: LegalPage[];
    reviews: WalkReviewSummary[];
    walk: Walk;
}

/** Preferred width for a waypoint's round preview thumbnail — only this single size is sent to the client, not the full Media object. */
const WAYPOINT_PREVIEW_IMAGE_WIDTH = 300;
/** Number of waypoint previews shown unlocked before the "N more locations" teaser kicks in. */
const FREE_PREVIEW_COUNT = 3;

/**
 * Walk-Detailseite. Zeigt alle Details eines Walks im Stitch-Design:
 * Hero, Beschreibung, Stat-Tiles, Karte, Wegpunkte-Preview, Guide-Info und CTA.
 *
 * @param props         Die Seiten-Props.
 * @param props.reviews Die freigegebenen Bewertungen des Walks.
 * @param props.walk    Das Walk-Dokument.
 * @returns ReactNode der Walk-Detailseite.
 */
const WalkPage: NextSSGPageWithLayout<typeof getStaticProps> = (
    {reviews, walk}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {
        adding,
        authLoading,
        categoryBadges,
        checkoutStatus,
        cityGeo,
        ctaLabel,
        durationLabel,
        guide,
        guideProfileImage,
        handleAddToLibrary,
        hasMapData,
        heroImages,
        heroImageUrl,
        jsonLd,
        markers,
        ogImage,
        priceLabel,
        statTiles,
        t,
        waypointPreviews
    } = useWalkDetailPage(walk, reviews);

    return (
        <Page data-cy="walk-detail">
            <SeoHead
                description={walk.meta?.description ?? walk.shortDescription}
                geo={cityGeo}
                image={ogImage}
                jsonLd={jsonLd}
                ogType="product"
                title={walk.meta?.title ?? walk.title}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/walks/${walk.slug}`}
            />
            {checkoutStatus === 'success' && (
                <StatusBanner $variant="success" data-cy="checkout-success-banner">
                    {t('checkout.successMessage')}
                </StatusBanner>
            )}
            {checkoutStatus === 'pending' && (
                <StatusBanner $variant="pending" data-cy="checkout-pending-banner">
                    {t('checkout.pendingMessage')}
                </StatusBanner>
            )}
            {checkoutStatus === 'cancelled' && (
                <StatusBanner $variant="cancelled" data-cy="checkout-cancelled-banner">
                    {t('checkout.cancelledMessage')}
                </StatusBanner>
            )}
            <HeroSection
                categories={categoryBadges}
                durationLabel={durationLabel}
                imageAlt={typeof walk.coverImage === 'object' ? (walk.coverImage.alt ?? '') : ''}
                images={heroImages}
                imageUrl={heroImageUrl}
                priceLabel={priceLabel}
                title={walk.title}
            />
            <Wrapper>
                <Main>
                    <DescriptionCard
                        body={walk.shortDescription}
                        title={walk.subtitle}
                    />
                    {hasMapData && (
                        <MapSection>
                            <WalkMap markers={markers} variant="preview" />
                        </MapSection>
                    )}
                    {waypointPreviews.length > 0 && (
                        <SectionSpacing $mobileOrder={3}>
                            <WaypointPreviewList
                                heading={t('walkDetail.waypoints')}
                                lockedCountLabel={
                                    waypointPreviews.length > FREE_PREVIEW_COUNT
                                        ? `${waypointPreviews.length - FREE_PREVIEW_COUNT} ${
                                            t('walkDetail.moreLockedLocations')}`
                                        : undefined
                                }
                                waypoints={waypointPreviews}
                            />
                        </SectionSpacing>
                    )}
                    {reviews.length > 0 && (
                        <SectionSpacing $mobileOrder={9}>
                            <ReviewsSection heading={t('walkDetail.reviews')} reviews={reviews} />
                        </SectionSpacing>
                    )}
                </Main>
                <Sidebar>
                    {!authLoading && (
                        <Visible lg xl xxl>
                            <StickyContainer>
                                <StickyActionButton
                                    disabled={adding}
                                    icon={<LockOpenIcon height={22} width={22} />}
                                    label={ctaLabel}
                                    onClick={handleAddToLibrary}
                                />
                            </StickyContainer>
                        </Visible>
                    )}
                    {statTiles.length > 0 && (
                        <SectionSpacing $mobileOrder={1}>
                            <StatTileGrid tiles={statTiles} />
                        </SectionSpacing>
                    )}
                    {guide && (
                        <SectionSpacing $mobileOrder={8}>
                            <GuideCard
                                avatarUrl={guideProfileImage?.sizes?.thumbnail?.url ?? undefined}
                                description={guide.shortDescription ?? undefined}
                                name={guide.name}
                                profileHref={`/guides/${guide.slug}`}
                                roleLabel={t('walkDetail.guide')}
                                websiteLabel={t('walkDetail.website')}
                                websiteUrl={guide.website ?? undefined}
                            />
                        </SectionSpacing>
                    )}
                </Sidebar>
            </Wrapper>
            {!authLoading && (
                <Visible md sm xs>
                    <StickyContainer>

                        <StickyActionButton
                            disabled={adding}
                            icon={<LockOpenIcon height={22} width={22} />}
                            label={ctaLabel}
                            onClick={handleAddToLibrary}
                        />
                    </StickyContainer>
                </Visible>
            )}
            {walk.copyrights && walk.copyrights.length > 0 && (
                <CopyrightSection data-cy="walk-copyrights">
                    {walk.copyrights.map(cr => (
                        <CopyrightLine key={cr.id ?? cr.text}>
                            {cr.text}{cr.source ? ` — ${cr.source}` : ''}
                        </CopyrightLine>
                    ))}
                </CopyrightSection>
            )}
        </Page>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout.
 *
 * @param _route        Der Next.js Router.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
WalkPage.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="home" legalPages={pageProps.legalPages} isBackVisible>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
WalkPage.getLayoutKey = () => 'walk';

export default WalkPage;

/* Styled Components */

const Page = styled.div`
    background: ${({theme}) => theme.colors.surfaceBackground};
    display: flex;
    flex-direction: column;
    gap: 4rem;
    min-height: 100dvh;
    padding-bottom: 0;
`;

const StatusBanner = styled.div<{$variant: 'cancelled' | 'pending' | 'success'}>`
    background: ${({$variant, theme}) => {
        if ($variant === 'success') return theme.colors.primaryDefault;
        if ($variant === 'pending') return theme.colors.accentDefault;

        return theme.colors.error;
    }};
    border-radius: 8px;
    color: white;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    margin: 0 ${spacing(6)};
    padding: ${spacing(3)} ${spacing(4)};
    text-align: center;
`;

const Wrapper = styled.div`
    display: contents;

    ${media('lg')} {
        display: grid;
        grid-template-columns: 70% 30%;
    }
`;
const Main = styled.main`
    display: contents;

    ${media('lg')} {
        display: flex;
        flex-direction: column;
        gap: 4rem;
    }
`;
const MapSection = styled.section`
    order: 2;
    padding: 0 2.4rem;
       ${media('lg')} {
        order: unset;
    }
`;

interface SectionSpacingProps {
    $mobileOrder?: number;
}

const SectionSpacing = styled.div<SectionSpacingProps>`
    display: flex;
    flex-direction: column;
    order: ${({$mobileOrder}) => $mobileOrder ?? 'unset'};
    padding: 0 ${spacing(6)};

    ${media('lg')} {
        order: unset
    }
`;

const Sidebar = styled.aside`
    display: contents;

    ${media('lg')} {
        display: flex;
        flex-direction: column;
        gap: 4rem;
    }
`;

const StickyContainer = styled.div`
    bottom: ${spacing(18)};
    order: 10;
    position: sticky;

    ${media('lg')} {
        bottom: unset;
        order: unset;
        top: ${spacing(4)};
    }
`;

const CopyrightSection = styled.footer`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    order: 999;
    padding: 0 2.4rem;
`;

const CopyrightLine = styled.p`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.1rem;
    line-height: 1.4;
    margin: 0;
`;

/**
 * Lädt alle Walk-Slugs für die statische Generierung.
 *
 * @returns Pfade und Fallback-Konfiguration.
 */
export const getStaticPaths: GetStaticPaths = () => ({
    fallback: 'blocking',
    paths: []
});

/**
 * Lädt das Walk-Dokument anhand des Slugs mit voller Tiefe für Guide, City und Galerie.
 *
 * @param context                  Der Next.js Static-Props Kontext.
 * @param context.locale           Die aktuelle Locale.
 * @param context.params           Die Route-Parameter mit Slug.
 * @param context.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit dem Walk-Dokument oder notFound.
 */
export const getStaticProps: GetStaticProps<WalkPageProps> = async ({locale, params, revalidateReason}) => {
    const slug = params?.slug as string;
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {notFound: true};
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const walks = await payload.find({
        collection: 'walks',
        depth: 2,
        joins: {waypoints: false},
        locale: payloadLocale,
        overrideAccess: true,
        select: {
            categories: true,
            city: true,
            copyrights: true,
            coverImage: true,
            gallery: true,
            general: true,
            guide: true,
            meta: true,
            pricing: true,
            shortDescription: true,
            slug: true,
            subtitle: true,
            targetAudience: true,
            title: true
        },
        where: {
            and: [
                {slug: {equals: slug}},
                {_status: {equals: 'published'}}
            ]
        }
    });

    const walk = walks.docs[0] as Walk | undefined;

    if (!walk) {
        return {notFound: true};
    }

    const [waypointsResult, reviewsResult, legalPages] = await Promise.all([
        payload.find({
            collection: 'waypoints',
            depth: 1,
            limit: 0,
            locale: payloadLocale,
            select: {
                images: true,
                location: true,
                shortDescription: true,
                title: true
            },
            sort: 'sortOrder',
            where: {walk: {equals: walk.id}}
        }),
        payload.find({
            collection: 'reviews',
            depth: 1,
            limit: 0,
            overrideAccess: true,
            select: {
                createdAt: true,
                rating: true,
                text: true,
                user: true
            },
            where: {
                and: [
                    {walk: {equals: walk.id}},
                    {status: {equals: 'approved'}}
                ]
            }
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    const leanReviews: WalkReviewSummary[] = reviewsResult.docs.map(review => ({
        authorName: abbreviateReviewerName((typeof review.user === 'object' ? review.user.name : null) ?? 'Anonym'),
        createdAt: review.createdAt,
        id: review.id,
        rating: review.rating,
        text: review.text
    }));

    const leanWalk = {
        ...walk,
        waypoints: {
            docs: waypointsResult.docs.map(wp => {
                const firstImage = wp.images?.[0]?.image;
                const previewImage = typeof firstImage === 'object' ? firstImage : null;
                const imageData = buildResponsiveImageData(previewImage, WAYPOINT_PREVIEW_IMAGE_WIDTH);

                return {
                    id: wp.id,
                    imageAlt: imageData?.alt,
                    imageUrl: imageData?.src,
                    location: wp.location,
                    shortDescription: wp.shortDescription,
                    title: wp.title
                };
            }),
            totalDocs: waypointsResult.totalDocs
        }
    };

    return {
        props: {
            legalPages,
            reviews: JSON.parse(JSON.stringify(leanReviews)) as WalkReviewSummary[],
            walk: JSON.parse(JSON.stringify(leanWalk)) as Walk
        },
        revalidate: 18000
    };
};